"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { BiEnvelopeOpen, BiMap, BiPhoneCall } from "react-icons/bi";
import { BsFacebook, BsInstagram, BsLinkedin, BsYoutube } from "react-icons/bs";
import { FaChevronUp } from "react-icons/fa";

import Logo from "../../../public/assets/melco-logo.webp";

const Footer = () => {
  const [showScroll, setShowScroll] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowScroll(window.scrollY > 200);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer
      role="contentinfo"
      aria-labelledby="footer-heading"
      className="bg-[#282930] text-gray-100 pt-16 pb-8 relative font-sans"
    >
      <div className="max-w-7xl mx-auto z-10 relative px-4 md:px-8">
        <h2 id="footer-heading" className="sr-only">
          Footer
        </h2>

        {/* === GRID WITH BALANCED SPACING === */}
        <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-12 items-start pt-4">
          
          {/* Column 1: Logo, Address, Email, Phone & Socials (Span 4) */}
          <div className="lg:col-span-4 flex flex-col gap-4">
            <Link href="/" className="inline-block">
              <Image
                src={Logo}
                alt="MELCO INDIA PVT. LTD."
                height={60}
                width={200}
                loading="lazy"
                className="w-52 sm:w-70 h-auto object-contain"
              />
            </Link>

            {/* Address, Email & Phone */}
            <ul role="list" className="flex flex-col gap-2.5 mt-1">
              <li>
                <Link
                  href="https://maps.google.com"
                  target="_blank"
                  className="flex items-start gap-3 text-gray-300 text-xs hover:text-[#ffa20d] transition-colors duration-200"
                >
                  <BiMap className="text-lg mt-0.5 shrink-0 text-[#ffa20d]" aria-hidden="true" />
                  <address className="not-italic leading-relaxed">
                    Melco India Pvt. Ltd. <br />
                    Plot No. 4, Sector - 27 A, Mathura Road <br />
                    Faridabad - 121003, Haryana (India)
                  </address>
                </Link>
              </li>
              <li>
                <Link
                  href="mailto:info@melcoindia.in"
                  className="flex items-center gap-3 text-gray-300 text-xs hover:text-[#ffa20d] transition-colors duration-200"
                >
                  <BiEnvelopeOpen
                    className="text-base shrink-0 text-[#ffa20d]"
                    aria-hidden="true"
                  />
                  <span>info@melcoindia.in</span>
                </Link>
              </li>
              <li>
                <Link
                  href="tel:+911292276466"
                  className="flex items-center gap-3 text-gray-300 text-xs hover:text-[#ffa20d] transition-colors duration-200"
                >
                  <BiPhoneCall className="text-base shrink-0 text-[#ffa20d]" aria-hidden="true" />
                  <span>+91-129-2276466</span>
                </Link>
              </li>
            </ul>

            {/* Social Icons */}
            <nav aria-label="Social media" className="mt-1">
              <ul role="list" className="flex items-center gap-5 pl-8">
                {[
                  { icon: <BsFacebook />, href: "#", label: "Facebook" },
                  { icon: <BsInstagram />, href: "#", label: "Instagram" },
                  { icon: <BsLinkedin />, href: "#", label: "LinkedIn" },
                  { icon: <BsYoutube />, href: "#", label: "YouTube" },
                ].map((item) => (
                  <li key={item.label}>
                    <Link
                      href={item.href}
                      target="_blank"
                      aria-label={item.label}
                      className="text-md  text-gray-300 hover:text-[#ffa20d] transition-colors duration-200"
                    >
                      {item.icon}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Column 2: Systems Links (Mobile: hidden | Tablet & Desktop: flex) */}
          <div className="hidden lg:flex lg:col-span-6 flex-col items-start gap-3">
            <h3 className="text-base font-bold text-white tracking-wide">
              Systems
            </h3>
            <nav aria-label="Product links" className="w-full">
              <ul role="list" className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-6 w-full">
                {[
                  { href: "/ash-handling-systems", text: "Ash Handling Systems" },
                  { href: "/bottom-ash-transporting-system", text: "Bottom Ash Transporting System" },
                  { href: "/coarse-ash-transporting-system", text: "Coarse Ash Transporting System" },
                  { href: "/fly-ash-transporting-system", text: "Fly Ash Transporting System" },
                  { href: "/ash-slurry-disposal-system", text: "Ash Slurry Disposal System" },
                  { href: "/coal-gasifier-ash-transporting-system", text: "Coal Gasifier Ash Transporting System" },
                  { href: "/ash-water-recirculation-systems", text: "Ash Water Recirculation Systems" },
                  { href: "/coal-mill-reject-handling-systems", text: "Coal Mill Reject Handling Systems" },
                  { href: "/pressure-conveying-system", text: "Pressure Conveying System" },
                  { href: "/belt-conveying-system", text: "Belt Conveying System" },
                ].map((item) => (
                  <li
                    key={item.text}
                    className="text-gray-400 text-xs hover:text-[#ffa20d] transition-colors duration-200"
                  >
                    <Link href={item.href}>{item.text}</Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Column 3: Quick Links (Mobile: hidden | Tablet & Desktop: flex) */}
          <div className="hidden md:flex lg:col-span-2 flex-col items-start gap-3">
            <h3 className="text-base font-bold text-white tracking-wide">
              Quick Links
            </h3>
            <ul role="list" className="flex flex-col gap-2.5">
              {[
                { href: "/infrastructure", text: "Infrastructure" },
                { href: "/company-profile", text: "Company Profile" },
                { href: "/quality-policy", text: "Quality Policy" },
                { href: "/careers", text: "Careers" },
              ].map((item) => (
                <li
                  key={item.text}
                  className="text-gray-400 text-xs hover:text-[#ffa20d] transition-colors duration-200"
                >
                  <Link href={item.href}>{item.text}</Link>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* Divider & Copyright */}
        <div className="mt-12 pt-6 border-t border-gray-700/60 flex flex-col sm:flex-row items-center justify-center relative">
          <p className="text-gray-400 text-xs text-center">
            ©{new Date().getFullYear()} MELCO INDIA PVT. LTD. | All rights reserved
          </p>
        </div>
      </div>

      {/* Scroll-To-Top Button */}
      {showScroll && (
        <button
          onClick={scrollToTop}
          aria-label="Scroll to top"
          className="fixed bottom-6 right-6 bg-[#ffa20d] text-slate-900 p-3 rounded-full shadow-lg hover:bg-amber-500 transition-all duration-300 z-50 focus:outline-none"
        >
          <FaChevronUp className="text-lg" />
        </button>
      )}
    </footer>
  );
};

export default Footer;